import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, ChevronRight, Clock, BookOpen, 
  Volume2, VolumeX, CheckCircle, AlertCircle, 
  FileText, HelpCircle, Award
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/context/AuthContext';
import { getChapter, getChapters, updateChapterTime } from '@/services/api';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PaginatedChapterPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [chapter, setChapter] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [timeSpent, setTimeSpent] = useState(0);
  
  // Quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizData, setQuizData] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [quizResult, setQuizResult] = useState(null);
  
  // Narrator state
  const [isNarrating, setIsNarrating] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('onyx');
  const audioRef = useRef(null);
  const speechRef = useRef(null);
  const ttsAbortRef = useRef(null);
  
  // Page read timer - enforces minimum read time per page
  const [pageTimeRemaining, setPageTimeRemaining] = useState(0);
  const pageTimerRef = useRef(null);

  const stopNarrator = useCallback(() => {
    if (ttsAbortRef.current) { ttsAbortRef.current.abort(); ttsAbortRef.current = null; }
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; audioRef.current = null; }
    window.speechSynthesis.cancel();
    setIsNarrating(false);
    setIsLoadingAudio(false);
  }, []);

  const timerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const startTimeRef = useRef(Date.now());
  const serverTimeRef = useRef(0); // Time already saved on server
  const lastSyncedTimeRef = useRef(0); // Last time we synced to server
  const timeSpentRef = useRef(0); // Ref mirror of timeSpent to avoid re-creating callbacks

  // Keep ref in sync with state
  useEffect(() => {
    timeSpentRef.current = timeSpent;
  }, [timeSpent]);

  // Sync time to server — uses ref so callback identity is stable
  const syncTimeToServer = useCallback(async () => {
    const totalTime = serverTimeRef.current + timeSpentRef.current;
    if (totalTime > lastSyncedTimeRef.current && id) {
      try {
        await updateChapterTime(id, totalTime);
        lastSyncedTimeRef.current = totalTime;
        console.log(`Time synced: ${totalTime}s`);
      } catch (err) {
        console.log('Could not sync time:', err);
      }
    }
  }, [id]);

  // Start timer on mount
  useEffect(() => {
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    
    // Sync time to server every 30 seconds
    syncTimerRef.current = setInterval(() => {
      syncTimeToServer();
    }, 30000);
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (syncTimerRef.current) {
        clearInterval(syncTimerRef.current);
      }
      if (pageTimerRef.current) {
        clearInterval(pageTimerRef.current);
      }
      // Final sync on unmount
      syncTimeToServer();
      stopNarrator();
    };
  }, [syncTimeToServer, stopNarrator]);

  // Fetch chapter and page info — runs ONCE on mount (component remounts per chapter via key={id})
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = sessionStorage.getItem('token');
        
        // Fetch chapter details
        const [chapterRes, chaptersRes] = await Promise.all([
          getChapter(id),
          getChapters()
        ]);
        setChapter(chapterRes.data);
        setChapters(chaptersRes.data);
        
        // Get existing time from server
        const currentChapter = chaptersRes.data.find(c => c.id === id);
        if (currentChapter && currentChapter.time_spent_seconds > 0) {
          serverTimeRef.current = currentChapter.time_spent_seconds;
          lastSyncedTimeRef.current = currentChapter.time_spent_seconds;
        }
        
        // Fetch page info
        const pageInfoRes = await fetch(`${API_URL}/api/chapters/${id}/pages/info`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const pageInfoData = await pageInfoRes.json();
        setPageInfo(pageInfoData);
        
        // Resume from last page if available, otherwise start at page 1
        const resumePage = pageInfoData.resume_page || 1;
        await fetchPage(resumePage);
        setCurrentPage(resumePage);
        
      } catch (err) {
        console.error('Failed to fetch chapter:', err);
        setError('Failed to load chapter content.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id]);

  const fetchPage = async (pageNum) => {
    stopNarrator();
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chapters/${id}/pages/${pageNum}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch page');
      
      const data = await res.json();
      setPageContent(data);
      setCurrentPage(pageNum);
      
      // Start page read timer
      if (pageTimerRef.current) clearInterval(pageTimerRef.current);
      const minSeconds = data.min_read_seconds || 30;
      setPageTimeRemaining(minSeconds);
      pageTimerRef.current = setInterval(() => {
        setPageTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(pageTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
      // Save progress to server (also enforces server-side time check)
      try {
        const saveRes = await fetch(`${API_URL}/api/chapters/${id}/pages/${pageNum}/save`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (saveRes.status === 403) {
          const errData = await saveRes.json();
          toast.error(errData.detail || 'Please wait before proceeding');
        }
      } catch (saveErr) {
        console.log('Could not save progress:', saveErr);
      }
      
      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
    } catch (err) {
      console.error('Failed to fetch page:', err);
      toast.error('Failed to load page');
    }
  };

  const fetchQuiz = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chapters/${id}/quiz/detailed`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error('Failed to fetch quiz');
      
      const data = await res.json();
      setQuizData(data);
      setShowQuiz(true);
      
    } catch (err) {
      console.error('Failed to fetch quiz:', err);
      toast.error('Failed to load quiz');
    }
  };

  const handleNextPage = () => {
    if (currentPage < pageInfo?.total_pages) {
      fetchPage(currentPage + 1);
    } else {
      // Show quiz after last page
      fetchQuiz();
    }
  };

  const handlePrevPage = () => {
    if (showQuiz) {
      setShowQuiz(false);
      setShowResults(false);
      fetchPage(pageInfo?.total_pages || 1);
    } else if (currentPage > 1) {
      fetchPage(currentPage - 1);
    }
  };

  const handleQuizAnswer = (questionNum, answer) => {
    setQuizAnswers(prev => ({ ...prev, [questionNum]: answer }));
  };

  const handleSubmitQuiz = async () => {
    if (!quizData) return;
    
    // Check if all questions answered
    const unanswered = quizData.questions.filter(q => !quizAnswers[q.number]);
    if (unanswered.length > 0) {
      toast.error(`Please answer all questions (${unanswered.length} remaining)`);
      return;
    }
    
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/chapters/${id}/quiz/submit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          answers: Object.fromEntries(
            Object.entries(quizAnswers).map(([k, v]) => [String(k), v])
          ),
          time_taken_seconds: timeSpentRef.current
        })
      });
      
      if (!res.ok) throw new Error('Failed to submit quiz');
      
      const result = await res.json();
      
      setQuizResult(result);
      setQuizSubmitted(true);
      setShowResults(true);
      
      if (result.passed) {
        toast.success(`Quiz passed! Score: ${result.percentage}%`);
      } else {
        toast.error(`Quiz not passed. Score: ${result.percentage}% (Need 75%)`);
      }
    } catch (err) {
      console.error('Quiz submit error:', err);
      toast.error('Failed to submit quiz. Please try again.');
    }
  };

  // Narrator - OpenAI TTS
  const handleNarrate = async () => {
    if (isNarrating || isLoadingAudio) {
      stopNarrator();
      return;
    }

    const contentDiv = document.querySelector('.page-content');
    if (!contentDiv) return;

    const textContent = contentDiv.innerText;
    const truncatedText = textContent.slice(0, 4096);

    if (truncatedText.length < 10) {
      toast.error('Not enough text content to narrate');
      return;
    }

    setIsLoadingAudio(true);

    // Create abort controller for this request
    const abortController = new AbortController();
    ttsAbortRef.current = abortController;

    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: truncatedText,
          voice: selectedVoice,
          speed: 1.0
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`TTS failed: ${response.status}`);
      }

      const data = await response.json();

      // Check if we were aborted while waiting
      if (abortController.signal.aborted) return;

      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;

        audio.onended = () => { setIsNarrating(false); audioRef.current = null; };
        audio.onerror = () => {
          setIsNarrating(false);
          audioRef.current = null;
          toast.error('Audio playback error');
        };

        await audio.play();
        setIsNarrating(true);
        setIsLoadingAudio(false);
        toast.success('AI Narrator started');
        return;
      }
      throw new Error('No audio data received');
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('TTS error:', error);
      toast.info('Using browser narrator (AI voice unavailable)');
      fallbackToWebSpeech(textContent);
    } finally {
      setIsLoadingAudio(false);
      ttsAbortRef.current = null;
    }
  };

  // Fallback to browser Web Speech API
  const fallbackToWebSpeech = (text) => {
    const utterance = new SpeechSynthesisUtterance(text.slice(0, 5000));
    utterance.rate = 0.9;
    utterance.pitch = 1;

    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.name.includes('Daniel') || v.name.includes('Google US English'));
    if (preferredVoice) utterance.voice = preferredVoice;

    utterance.onend = () => setIsNarrating(false);
    utterance.onerror = () => setIsNarrating(false);

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsNarrating(true);
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const currentIndex = chapters.findIndex(c => c.id === id);
  const prevChapter = currentIndex > 0 ? chapters[currentIndex - 1] : null;
  const nextChapter = currentIndex < chapters.length - 1 ? chapters[currentIndex + 1] : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-primary">Loading chapter...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">{error}</h2>
          <Button onClick={() => navigate('/dashboard')} className="mt-4 btn-primary">
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  // Calculate quiz score for results
  const getQuizScore = () => {
    if (quizResult) {
      return {
        correct: quizResult.score,
        total: quizResult.total,
        percentage: quizResult.percentage
      };
    }
    if (!quizData || !quizSubmitted) return { correct: 0, total: 0, percentage: 0 };
    const correct = quizData.questions.filter(q => 
      quizAnswers[q.number]?.toUpperCase() === q.correct_answer.toUpperCase()
    ).length;
    return {
      correct,
      total: quizData.questions.length,
      percentage: Math.round((correct / quizData.questions.length) * 100)
    };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#1e3a5f] text-white py-6">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/70 text-sm mb-1">CHAPTER {chapter?.chapter_number} OF 10</p>
              <h1 className="text-2xl font-bold">{chapter?.title}</h1>
            </div>
            <Button 
              variant="ghost" 
              onClick={async () => {
                await syncTimeToServer();
                navigate('/dashboard');
              }}
              className="text-white hover:bg-white/10"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 text-gray-600">
                <Clock className="w-4 h-4" />
                <span className="font-mono text-sm">{formatTime(timeSpent)}</span>
              </div>
              {!showQuiz && pageInfo && (
                <div className="flex items-center gap-2 text-gray-600">
                  <FileText className="w-4 h-4" />
                  <span className="text-sm">Page {currentPage} of {pageInfo.total_pages}</span>
                </div>
              )}
              {showQuiz && (
                <div className="flex items-center gap-2 text-[#1e3a5f]">
                  <HelpCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Chapter Quiz</span>
                </div>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleNarrate}
              disabled={isLoadingAudio}
              className={isNarrating ? "bg-[#1e3a5f] text-white" : ""}
            >
              {isLoadingAudio ? (
                <>
                  <div className="w-4 h-4 mr-2 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  Loading...
                </>
              ) : isNarrating ? (
                <>
                  <VolumeX className="w-4 h-4 mr-2" />
                  Stop
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4 mr-2" />
                  AI Narrator
                </>
              )}
            </Button>
          </div>
          
          {/* Page progress */}
          {pageInfo && (
            <div className="mt-3">
              <Progress 
                value={showQuiz ? 100 : (currentPage / pageInfo.total_pages) * 100} 
                className="h-1"
              />
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        {!showQuiz ? (
          /* Page Content */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {pageContent && (
              <>
                <div className="text-sm text-gray-500 mb-4">
                  {pageContent.title}
                </div>
                <div 
                  className="page-content prose prose-slate max-w-none chapter-content"
                  dangerouslySetInnerHTML={{ __html: pageContent.content }}
                />
              </>
            )}
          </div>
        ) : showResults ? (
          /* Quiz Results */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              {getQuizScore().percentage >= 75 ? (
                <div className="text-green-600">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold">Quiz Passed!</h2>
                </div>
              ) : (
                <div className="text-red-600">
                  <AlertCircle className="w-16 h-16 mx-auto mb-4" />
                  <h2 className="text-2xl font-bold">Quiz Not Passed</h2>
                </div>
              )}
              <p className="text-xl mt-2">
                Score: {getQuizScore().correct}/{getQuizScore().total} ({getQuizScore().percentage}%)
              </p>
              <p className="text-gray-600">Passing score: 75%</p>
            </div>
            
            {/* Focused Remediation — Only shows when there are missed questions */}
            {(() => {
              const missed = quizData?.questions.filter(q => 
                quizAnswers[q.number]?.toUpperCase() !== q.correct_answer.toUpperCase()
              ) || [];
              if (missed.length === 0) return null;
              return (
                <div className="mb-10 p-6 bg-amber-50 border-l-4 border-amber-500 rounded">
                  <h3 className="text-xl font-bold mb-2 text-amber-900 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Questions to Review ({missed.length})
                  </h3>
                  <p className="text-sm text-amber-800 mb-6">
                    The questions below were answered incorrectly. Review the correct answer and the explanation. The complete answer review for the quiz is shown further down the page.
                  </p>
                  {missed.map((q, idx) => {
                    const userAnswer = quizAnswers[q.number];
                    const correctOption = q.options?.find(o => o.letter === q.correct_answer);
                    return (
                      <div key={`miss-${idx}`} className="mb-6 p-5 bg-white border border-amber-200 rounded shadow-sm" data-testid={`missed-question-${q.number}`}>
                        <div className="flex items-start gap-3 mb-3">
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-600 flex items-center justify-center text-white font-bold text-sm">
                            {q.number}
                          </span>
                          <p className="font-medium text-gray-900">{q.question}</p>
                        </div>
                        <div className="ml-10 space-y-3">
                          <div className="text-sm">
                            <span className="text-gray-500">Your answer: </span>
                            <span className="font-medium text-red-700">
                              {userAnswer ? `${userAnswer}. ${q.options?.find(o => o.letter === userAnswer)?.text || ''}` : 'Not answered'}
                            </span>
                          </div>
                          {correctOption && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded">
                              <div className="text-sm text-green-700 font-semibold mb-1">✓ Correct answer</div>
                              <div className="font-medium text-gray-900">{correctOption.letter}. {correctOption.text}</div>
                            </div>
                          )}
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded">
                            <div className="text-sm font-semibold text-gray-700 mb-1">Why this is the answer</div>
                            <p className="text-sm text-gray-700">{q.explanation}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
            
            <h3 className="text-xl font-bold mb-6 border-b pb-2">Answer Review</h3>
            
            {quizData?.questions.map((q, idx) => {
              const userAnswer = quizAnswers[q.number];
              const isCorrect = userAnswer?.toUpperCase() === q.correct_answer.toUpperCase();
              
              return (
                <div key={idx} className={`mb-8 p-6 rounded-lg ${isCorrect ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  <div className="flex items-start gap-3 mb-4">
                    <span className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white font-bold ${isCorrect ? 'bg-green-600' : 'bg-red-600'}`}>
                      {q.number}
                    </span>
                    <p className="font-medium text-gray-800">{q.question}</p>
                  </div>
                  
                  {q.type === 'multiple_choice' && q.options && (
                    <div className="ml-11 space-y-2 mb-4">
                      {q.options.map(opt => (
                        <div 
                          key={opt.letter}
                          className={`p-2 rounded ${
                            opt.letter === q.correct_answer 
                              ? 'bg-green-100 border border-green-300 font-medium' 
                              : userAnswer === opt.letter && !isCorrect
                                ? 'bg-red-100 border border-red-300'
                                : 'bg-white'
                          }`}
                        >
                          <span className="font-medium mr-2">{opt.letter}.</span>
                          {opt.text}
                          {opt.letter === q.correct_answer && <span className="ml-2 text-green-700">✓ Correct</span>}
                          {userAnswer === opt.letter && opt.letter !== q.correct_answer && <span className="ml-2 text-red-700">✗ Your answer</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {q.type === 'true_false' && (
                    <div className="ml-11 mb-4">
                      <p><strong>Correct Answer:</strong> {q.correct_answer}</p>
                      <p><strong>Your Answer:</strong> {userAnswer || 'Not answered'}</p>
                    </div>
                  )}
                  
                  <div className="ml-11 p-4 bg-white rounded border">
                    <p className="text-sm font-medium text-gray-700 mb-1">Explanation:</p>
                    <p className="text-gray-600">{q.explanation}</p>
                  </div>
                </div>
              );
            })}
            
            {quizData?.bonus_question && (
              <div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-bold text-lg mb-4 flex items-center gap-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  Bonus Question
                </h4>
                <p className="mb-4">{quizData.bonus_question.question}</p>
                <div className="bg-white p-4 rounded border">
                  <p className="text-sm font-medium text-gray-700 mb-2">Model Answer:</p>
                  <p className="text-gray-600 whitespace-pre-line">{quizData.bonus_question.model_answer}</p>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* Quiz Questions */
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-[#1e3a5f]">{quizData?.title}</h2>
              <p className="text-gray-600">{quizData?.subtitle}</p>
              <p className="text-sm text-gray-500 mt-2">Answer all questions, then click Submit to see your results with detailed explanations.</p>
            </div>
            
            {quizData?.questions.map((q, idx) => (
              <div key={idx} className="mb-8 p-6 bg-gray-50 rounded-lg">
                <div className="flex items-start gap-3 mb-4">
                  <span className="flex-shrink-0 w-8 h-8 bg-[#1e3a5f] rounded-full flex items-center justify-center text-white font-bold">
                    {q.number}
                  </span>
                  <p className="font-medium text-gray-800">{q.question}</p>
                </div>
                
                {q.type === 'multiple_choice' && q.options && (
                  <RadioGroup 
                    value={quizAnswers[q.number] || ''} 
                    onValueChange={(value) => handleQuizAnswer(q.number, value)}
                    className="ml-11 space-y-2"
                  >
                    {q.options.map(opt => (
                      <div key={opt.letter} className="flex items-center space-x-3 p-3 bg-white rounded border hover:border-[#1e3a5f] transition-colors">
                        <RadioGroupItem value={opt.letter} id={`q${q.number}-${opt.letter}`} />
                        <Label htmlFor={`q${q.number}-${opt.letter}`} className="cursor-pointer flex-1">
                          <span className="font-medium mr-2">{opt.letter}.</span>
                          {opt.text}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                
                {q.type === 'true_false' && (
                  <RadioGroup 
                    value={quizAnswers[q.number] || ''} 
                    onValueChange={(value) => handleQuizAnswer(q.number, value)}
                    className="ml-11 flex gap-4"
                  >
                    <div className="flex items-center space-x-2 p-3 bg-white rounded border hover:border-[#1e3a5f] transition-colors">
                      <RadioGroupItem value="TRUE" id={`q${q.number}-true`} />
                      <Label htmlFor={`q${q.number}-true`} className="cursor-pointer font-medium">TRUE</Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 bg-white rounded border hover:border-[#1e3a5f] transition-colors">
                      <RadioGroupItem value="FALSE" id={`q${q.number}-false`} />
                      <Label htmlFor={`q${q.number}-false`} className="cursor-pointer font-medium">FALSE</Label>
                    </div>
                  </RadioGroup>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevPage}
            disabled={currentPage === 1 && !showQuiz}
            className="flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            {showQuiz ? "Back to Content" : "Previous Page"}
          </Button>
          
          {!showQuiz ? (
            <Button
              onClick={handleNextPage}
              disabled={pageTimeRemaining > 0 && user?.role !== 'admin'}
              className={`${(pageTimeRemaining > 0 && user?.role !== 'admin') ? 'bg-gray-500 cursor-not-allowed' : 'bg-[#1e3a5f] hover:bg-[#2d4a6f]'} text-white flex items-center gap-2`}
              data-testid="next-page-btn"
            >
              {(pageTimeRemaining > 0 && user?.role !== 'admin') ? (
                <>
                  <Clock className="w-4 h-4" />
                  {Math.floor(pageTimeRemaining / 60)}:{String(pageTimeRemaining % 60).padStart(2, '0')}
                </>
              ) : (
                <>
                  {currentPage < (pageInfo?.total_pages || 1) ? "Next Page" : "Take Quiz"}
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </Button>
          ) : !showResults ? (
            <Button
              onClick={handleSubmitQuiz}
              className="bg-green-600 hover:bg-green-700 text-white flex items-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Submit Quiz
            </Button>
          ) : (
            <div className="flex gap-4">
              {getQuizScore().percentage < 75 && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setQuizAnswers({});
                    setQuizSubmitted(false);
                    setShowResults(false);
                    setQuizResult(null);
                  }}
                >
                  Retry Quiz
                </Button>
              )}
              {nextChapter && getQuizScore().percentage >= 75 && (
                <Button
                  onClick={async () => {
                    await syncTimeToServer();
                    navigate(`/chapter/${nextChapter.id}`);
                  }}
                  className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white"
                >
                  Next Chapter
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Chapter Content Styles */}
      <style>{`
        .chapter-content {
          font-family: 'Georgia', serif;
          line-height: 1.8;
          color: #1a1a1a;
        }

        .chapter-content h1 {
          font-size: 2rem;
          color: #1e3a5f;
          margin-bottom: 1rem;
          font-weight: 700;
        }

        .chapter-content h2 {
          font-size: 1.5rem;
          color: #1e3a5f;
          margin-top: 2rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 1px solid #e5e7eb;
        }

        .chapter-content h3 {
          font-size: 1.25rem;
          color: #374151;
          margin-top: 1.5rem;
          margin-bottom: 0.75rem;
        }

        .chapter-content h4 {
          font-size: 1.1rem;
          color: #374151;
          margin-top: 1rem;
          margin-bottom: 0.5rem;
        }

        .chapter-content p {
          margin-bottom: 1rem;
        }

        .chapter-content ul, .chapter-content ol {
          margin-bottom: 1rem;
          padding-left: 1.5rem;
        }

        .chapter-content li {
          margin-bottom: 0.5rem;
        }

        .chapter-content table {
          width: 100%;
          border-collapse: collapse;
          margin: 1.5rem 0;
        }

        .chapter-content th {
          background: #1e3a5f;
          color: white;
          padding: 0.75rem 1rem;
          text-align: left;
          font-weight: 600;
        }

        .chapter-content td {
          padding: 0.75rem 1rem;
          border-bottom: 1px solid #e5e7eb;
          vertical-align: top;
        }

        .chapter-content tr:nth-child(even) {
          background: #f9fafb;
        }

        .chapter-content .key-principle {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          padding: 1.5rem;
          border-radius: 0.5rem;
          margin: 1.5rem 0;
        }

        .chapter-content .critical-warning {
          background: #fef3c7;
          color: #78350f;
          border-left: 4px solid #f59e0b;
          padding: 1.5rem;
          border-radius: 0.25rem;
          margin: 1.5rem 0;
        }

        .chapter-content .critical-warning p {
          color: #78350f;
        }

        .chapter-content .critical-warning strong {
          color: #78350f;
        }

        .chapter-content .checkpoint-box {
          background: #ecfdf5;
          border: 1px solid #10b981;
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin: 1.5rem 0;
        }

        .chapter-content .field-experience {
          background: #f0f9ff;
          border: 1px solid #0ea5e9;
          border-radius: 0.75rem;
          padding: 2rem;
          margin: 2rem 0;
        }

        .chapter-content .legal-table {
          margin: 2rem 0;
        }

        .chapter-content .bond-type {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.75rem;
          padding: 2rem;
          margin: 2rem 0;
        }

        .chapter-content .process-step {
          padding: 1.5rem;
          border-left: 3px solid #1e3a5f;
          margin: 1.5rem 0;
          background: #f9fafb;
        }

        .chapter-content .forfeiture-stage {
          margin: 2rem 0;
          padding: 1.5rem;
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
        }

        .chapter-content .motion-opportunity {
          background: #ecfdf5;
          border: 1px solid #10b981;
          padding: 1rem;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }

        .chapter-content .critical-deadline {
          background: #fef2f2;
          border: 1px solid #ef4444;
          padding: 1rem;
          border-radius: 0.5rem;
          margin: 1rem 0;
        }

        .chapter-content .authority-table tr.no-authority {
          background: #fef2f2;
        }

        .chapter-content .authority-table tr.no-authority td {
          color: #991b1b;
          font-weight: 600;
        }

        .chapter-content .final-principle {
          background: #1e3a5f;
          color: white;
          padding: 2rem;
          border-radius: 0.75rem;
          margin-top: 2rem;
        }

        .chapter-content .final-principle h3,
        .chapter-content .final-principle p {
          color: white;
        }

        .chapter-content .role-box {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin: 1rem 0;
        }

        .chapter-content .role-box.highlighted {
          background: #f0f9ff;
          border-color: #0ea5e9;
        }

        .chapter-content .reporting-method {
          margin: 1.5rem 0;
          padding: 1rem;
          background: #f9fafb;
          border-radius: 0.5rem;
        }

        .chapter-content .document-section {
          margin: 2rem 0;
        }

        .chapter-content .document-item {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 0.5rem;
          padding: 1.5rem;
          margin: 1rem 0;
        }

        .chapter-content .verification-checklist {
          background: #fef3c7;
          border: 2px solid #f59e0b;
          border-radius: 0.75rem;
          padding: 2rem;
          margin: 2rem 0;
        }
      `}</style>
    </div>
  );
};

export default PaginatedChapterPage;
